import { useQuery } from '@tanstack/react-query';

import { StepExecution } from '@frontend/types/stepExecution';

import { agentExecutionsService } from '../services/agentExecutions';

/**
 * Hook to fetch memory details for a step execution using the HATEOAS memory link.
 */
export function useStepMemoryDetails(stepExecution: StepExecution | null, shouldPoll = false) {
  const memoryUrl = stepExecution?.links?.memory;

  return useQuery({
    queryKey: ['stepMemoryDetails', stepExecution?.step_id, stepExecution?.memory_id],
    queryFn: () => {
      if (!memoryUrl) throw new Error('memoryUrl is required');
      return agentExecutionsService.getMemoryDetails(memoryUrl);
    },
    enabled: !!memoryUrl,
    staleTime: shouldPoll ? 0 : 1000 * 60, // No caching when polling, otherwise cache for 1 minute
    refetchInterval: shouldPoll ? 5000 : false, // Poll every 5 seconds when shouldPoll is true
    retry: 1,
  });
}

/**
 * Hook to fetch memory chunks for a step execution.
 * Requires the memory details to get the chunks link.
 */
export function useStepMemoryChunks(stepExecution: StepExecution | null, shouldPoll = false) {
  // First get memory details to get the chunks link
  const { data: memoryDetails } = useStepMemoryDetails(stepExecution, shouldPoll);

  const chunksUrl = memoryDetails?.links?.chunks;

  return useQuery({
    queryKey: ['stepMemoryChunks', stepExecution?.step_id, stepExecution?.memory_id, chunksUrl],
    queryFn: () => {
      if (!chunksUrl) throw new Error('chunksUrl is required');
      return agentExecutionsService.getMemoryChunks(chunksUrl);
    },
    enabled: !!chunksUrl,
    staleTime: shouldPoll ? 0 : 1000 * 60, // No caching when polling, otherwise cache for 1 minute
    refetchInterval: shouldPoll ? 5000 : false, // Poll every 5 seconds when shouldPoll is true
    retry: 1,
  });
}

/**
 * Composite hook that provides both memory details and chunks for a step execution.
 * This is a convenience hook that combines the above two hooks.
 */
export function useStepMemory(stepExecution: StepExecution | null, shouldPoll = false) {
  const memoryQuery = useStepMemoryDetails(stepExecution, shouldPoll);
  const chunksQuery = useStepMemoryChunks(stepExecution, shouldPoll);

  return {
    memory: {
      data: memoryQuery.data,
      isPending: memoryQuery.isPending,
      error: memoryQuery.error,
    },
    chunks: {
      data: chunksQuery.data,
      isPending: chunksQuery.isPending,
      error: chunksQuery.error,
    },
    isLoading: memoryQuery.isPending || chunksQuery.isPending,
    hasError: !!memoryQuery.error || !!chunksQuery.error,
  };
}
